import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const aiVoiceoverVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.aiVoiceoverVideoBasics,
    description: `Create or edit an AI-generated voiceover — drafting/generating narration from a script, choosing a voice, and the pause/reword/re-voice/pronunciation mechanics. Load when the video is driven by an AI voiceover (the default), or when editing one. For the shared overlay/coverage/scene-group mechanics, use ${SKILL_NAMES.voiceoverVideoBasics}.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# AI Voiceover Video Basics

Creating and editing an AI-generated voiceover. The shared scene-group mechanics —
overlay coverage, splits, reorders, re-timing, targeting — live in
\`${SKILL_NAMES.voiceoverVideoBasics}\`; this skill is the AI-specific part.

## Creating the voiceover
The voiceover is generated from a script. If the user hasn't given you one, analyze what
they've provided and use the questionnaire to ask clarifying questions (offer topics, etc).
Then write the script.
- \`${TOOL_NAMES.addVoiceoverSceneGroup}\` generates the AI voiceover audio and its synced
  transcript in one step from the **plain** script. Create a single voiceover scene
  group for the whole video (never one per scene), then build overlays over it.
- If the user requested a specific voice, find it with \`${TOOL_NAMES.searchVoices}\` first and pass
  its slug.

## Adding pauses
Pauses give narration room to breathe. The two-step procedure:
1. Generate the voiceover from the **plain** script (\`${TOOL_NAMES.addVoiceoverSceneGroup}\` —
   pause tags in the initial transcript would be read aloud).
2. Add pauses with \`${TOOL_NAMES.editAiVoiceover}\`, inserting \`<pause seconds="N"/>\` tokens in
   the replacement text.

Placement taste: after the opening hook, before a key point, ahead of the closing
line. Intentional and sparing — not between every sentence.

Once pauses exist they are transcript tokens like words: any word range you pass
(\`${TOOL_NAMES.addVoiceoverScene}\`, \`${TOOL_NAMES.splitSceneGroup}\`) must include the pause tokens
that fall inside it, written exactly as they appear.

## Pause token mechanics (${TOOL_NAMES.editAiVoiceover})
The transcript is one stream of tokens; a pause is just another token, written
\`<pause seconds="N"/>\` exactly as shown. \`find_phrase\`/\`replacement\` are literal
token find-and-replace: \`find_phrase\` must match one run of the transcript exactly
and uniquely — every word AND every \`<pause/>\` inside the run must appear in it (a
\`<pause/>\` in \`find_phrase\` matches any present pause; its N is ignored on match).
The matched run is replaced token-for-token; everything outside is untouched.
- **Add** a pause: include a \`<pause/>\` tag in the replacement.
- **Remove** one: match it in \`find_phrase\` and omit it from the replacement.
- **Move or resize** one: match it, then reposition it / change its N in the
  replacement. N is a whole number of seconds, 1-9; a fractional pause (from
  recorded audio, e.g. \`<pause seconds="2.3"/>\`) is kept by writing the nearest
  whole second.
- A \`<pause/>\` inside the run that's left out of \`find_phrase\` makes the match
  fail — include every one.

<example goal="add a word before an opening pause while keeping the pause">
  \`find_phrase\`: \`<pause seconds="1"/> Cupping is\`
  \`replacement\`: \`Hello. <pause seconds="1"/> Cupping is\`
</example>

## Rewording, re-voicing, speed
All three operations go through \`${TOOL_NAMES.editAiVoiceover}\`. A phrase replacement re-synthesizes only
the changed span; a voice or speed change re-synthesizes the whole voiceover.
To drop words, pass them as \`find_phrase\` with an empty \`replacement\`; a scene left with
no words is removed.
To get a fresh take of a stretch that sounds wrong (a glitch, or the user hears words that aren't
in the transcript), pass the same text as both \`find_phrase\` and \`replacement\`. That regenerates
the stretch without changing its words.
Overlays and scene timings re-align automatically — but re-check overlay text
afterwards, since on-screen text must stay within what the new words actually say.

## Fixing a mispronounced word
Only when the user reports a mispronunciation or asks for a specific pronunciation — never
scan a script for hard words or add pronunciations on your own.
- Use \`${TOOL_NAMES.fixPronunciation}\`. It updates every occurrence of the word or phrase
  (1-3 words, written as it appears in the transcript) in that scene group and re-voices it.
  The transcript and captions keep the correct spelling, so word ranges don't change.
- If the user gave a pronunciation, use it as given. Otherwise write a phonetic respelling:
  split into syllables and capitalize the stressed one (\`LOO-men five\`), spell initialisms
  as separate letters (\`S Q L\`), or write a word the way it sounds (\`sequel\`).
- You can't hear the result. Say what you set and ask the user to listen. If it still sounds
  wrong, try a different respelling style rather than resubmitting the same one.
- Don't respell words in the transcript with \`${TOOL_NAMES.editAiVoiceover}\` to fix pronunciation,
  and never regenerate the same words or change only punctuation and report it fixed — neither
  changes how a word is said.
- The fix applies to this video only. If the user wants it pronounced that way in future videos
  too, point them to the team's Pronunciation Library (video settings), or the transcript's
  "Fix pronunciation" action with "Apply override to all instances" checked.`;
    },
};
